import React, { useState } from 'react';
import dscService from '../../services/dscService';

const DSCModal = ({ visible, dataToSign, onSuccess, onClose, onError }) => {
    const [loading, setLoading] = useState(false);
    const [pin, setPin] = useState('');
    const [pinError, setPinError] = useState('');
    const [step, setStep] = useState('');
    const [error, setError] = useState('');

    if (!visible) return null;

    const handleSubmit = async (e) => {
        e.preventDefault();

        console.log('🔐 DSC Modal: Form submitted');

        if (!pin || pin.length < 4) {
            const errMsg = 'Please enter a valid PIN (minimum 4 digits)';
            setPinError(errMsg);
            if (onError) onError(new Error(errMsg));
            return;
        }

        setPinError('');
        setLoading(true);
        setError('');

        try {
            setStep('Checking DSC Helper...');
            const helperStatus = await dscService.checkLocalHelper();

            if (!helperStatus.success) {
                throw new Error('DSC Helper is not running. Please start the DSC Helper app and try again.');
            }

            setStep('Preparing document for DSC signing...');
            console.log('📝 Step 1: Preparing document...');

            const prepareResult = await dscService.prepareDSC(dataToSign);
            console.log('📡 Prepare result:', prepareResult);

            if (!prepareResult.success) {
                throw new Error(prepareResult.message || 'Failed to prepare payload');
            }

            setStep('Waiting for local DSC client...');
            console.log('📡 Calling signViaLocalClient...');

            const clientResult = await dscService.signViaLocalClient({
                payloadBase64: prepareResult.payloadBase64,
                hashBase64: prepareResult.hashBase64,
                algorithm: prepareResult.algorithm,
                pin: pin
            });
            console.log('📡 Client result:', clientResult);

            if (!clientResult.success) {
                // Handle specific client errors
                if (clientResult.error === 'PKCS11_LIBRARY_NOT_FOUND') {
                    throw new Error('❌ DSC Driver not installed. Please install your DSC token driver.');
                } else if (clientResult.error === 'TOKEN_NOT_PRESENT') {
                    throw new Error('❌ No USB token found. Please insert your DSC pendrive.');
                } else if (clientResult.error === 'INVALID_PIN') {
                    throw new Error('❌ Invalid PIN. Please check and try again.');
                } else if (clientResult.error === 'PIN_LOCKED') {
                    throw new Error('🔒 Token is locked. Please contact administrator.');
                } else {
                    throw new Error(clientResult.message || 'Client-side signing failed');
                }
            }

            setStep('Verifying digital signature...');
            console.log('📡 Calling verifyDSCSignature...');

            const verifyResult = await dscService.verifyDSCSignature({
                data: dataToSign,
                payloadBase64: prepareResult.payloadBase64,
                signature: clientResult.signature,
                certificatePem: clientResult.certificatePem,
                algorithm: prepareResult.algorithm
            });
            console.log('📡 Verify result:', verifyResult);

            if (!verifyResult.success) {
                // Handle DSC mismatch error
                if (verifyResult.error === 'DSC_MISMATCH') {
                    const registeredDate = verifyResult.registeredAt 
                        ? new Date(verifyResult.registeredAt).toLocaleDateString() 
                        : 'earlier';
                    throw new Error(`❌ Different DSC detected!\n\nYou must use your registered DSC token.\n\nRegistered on: ${registeredDate}`);
                } else if (verifyResult.error === 'INVALID_SIGNATURE') {
                    throw new Error('❌ Invalid signature. Please try again with correct PIN.');
                } else {
                    throw new Error(verifyResult.message || 'Signature verification failed');
                }
            }

            console.log('✅ All steps completed successfully');

            onSuccess({
                signature: clientResult.signature,
                certificate: clientResult.certificatePem,
                verification: verifyResult
            });

            setLoading(false);
            setStep('');
            setPin('');
            setError('');
        } catch (err) {
            console.error('❌ DSC signing error:', err);
            setLoading(false);
            setStep('');
            setError(err.message || 'DSC signing failed');
            if (onError) onError(err);
        }
    };

    return (
        <div className="dsc-modal-overlay">
            <div className="dsc-modal-container">
                <div className="dsc-modal-header">
                    <h2>🔐 Digital Signature Required</h2>
                    <button className="dsc-modal-close" onClick={onClose} disabled={loading}>✕</button>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="dsc-modal-body">
                        <p>
                            Your DSC token will be accessed on this computer using the local DSC client.
                            Please keep your USB token inserted and DSC Helper running.
                        </p>

                        <div className="dsc-input-group">
                            <label>DSC PIN:</label>
                            <input
                                type="password"
                                value={pin}
                                onChange={(e) => {
                                    setPin(e.target.value);
                                    setPinError('');
                                    setError('');
                                }}
                                placeholder="Enter your DSC PIN"
                                autoFocus
                                disabled={loading}
                                maxLength={16}
                            />
                            {pinError && (
                                <div className="dsc-field-error">
                                    ❌ {pinError}
                                </div>
                            )}
                        </div>

                        {step && (
                            <div className="dsc-info-message">
                                ⏳ {step}
                            </div>
                        )}

                        {error && (
                            <div className="dsc-error-message" style={{ whiteSpace: 'pre-line' }}>
                                {error}
                            </div>
                        )}
                    </div>

                    <div className="dsc-modal-footer">
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={loading}
                            className="dsc-btn-secondary"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="dsc-btn-primary"
                        >
                            {loading ? 'Processing...' : 'Sign & Submit'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default DSCModal;